import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import * as React from "react";
import styled from "styled-components";

import {
  FallbackContainer,
  MarketplaceProduct,
} from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/MarketplaceProduct";
import { ProductImageSection } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/ProductImageSection";
import { useProductTileClickImpressions } from "@faire/retailer/hooks/ProductTileImpression/useProductTileClickImpressions";
import { useProductTileViewImpression } from "@faire/retailer/hooks/ProductTileImpression/useProductTileViewImpression";
import { preloadNavigationToPDP } from "@faire/retailer/views/product/preload/preloadNavigationToPDP";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { productClickForSearchPageParametersILeanProductTileAdapter } from "../ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";

export const ProductImageOnlyTile: React.FC<
  | Omit<
      MarketplaceProduct.IProps,
      | "onQuickAddSubmit"
      | "onHeartClicked"
      | "onSaveToBoard"
      | "onRemoveFromBoard"
      | "onBrandClick"
      | "onProductVisible"
    > & { showPriceBadge?: boolean }
> = (props) => {
  const {
    recordAdImpression,
    productTile,
    brand,
    role,
    hideCartQuantity,
    showPriceBadge,
    className,
    generateProductLinkProps,
    showVerticalImagery,
    altTag,
    useLazyImageLoading,
    onClick,
    onQuickAdd,
    onChooseOption,
    onChooseOptionQuantityUpdate,
  } = props;

  const trackProductTileProps = useProductTileViewImpression({
    productToken: productTile?.product?.token,
    promotedBy: productTile?.product?.promoted_by,
    impressionId: productTile?.product?.impression_id,
    turnOffTracking: !recordAdImpression,
    settingsToAssignOnView: productTile?.settings_to_assign_on_view,
  });
  const {
    recordProductTileClickEvent,
    recordQuickAddButtonClickEvent,
    recordQuickAddSubmitEvent,
    recordChooseOptionClickEvent,
    recordHeartClickedEvent,
    recordSaveToBoardSubmitEvent,
    recordRemoveFromBoardSubmitEvent,
    recordProductBadgeViewEvent,
    recordProductTileRightClickEvent,
    recordChooseOptionQuantityUpdateSubmitEvent,
  } = useProductTileClickImpressions(
    productClickForSearchPageParametersILeanProductTileAdapter(productTile),
    brand?.token,
    recordAdImpression
  );

  const ID = `product-tile-${productTile?.product?.token}`;

  const isLoaded = !!productTile;

  const onProductClick = (
    productTile: ILeanProductTile,
    event: React.MouseEvent<HTMLElement>
  ) => {
    preloadNavigationToPDP({
      productToken: productTile.product?.token,
      bestImage: productTile.best_image,
      images: productTile.images,
      showVerticalImagery,
    });
    recordProductTileClickEvent();
    if (productTile.product) {
      onClick?.(productTile.product, event);
    }
  };

  const handleQuickAdd = () => {
    onQuickAdd?.();
    recordQuickAddButtonClickEvent();
  };
  const handleChooseOption = () => {
    onChooseOption?.();
    recordChooseOptionClickEvent();
  };
  const handleChooseOptionQuantityUpdate = () => {
    onChooseOptionQuantityUpdate?.();
    recordChooseOptionQuantityUpdateSubmitEvent();
  };

  return (
    <Container
      {...trackProductTileProps}
      onContextMenu={recordProductTileRightClickEvent}
    >
      <FallbackContainer
        isLoaded={isLoaded}
        id={ID}
        role={role}
        data-test-id={isLoaded ? "product-tile" : "product-tile-placeholder"}
        className={className}
      >
        <ProductImageSection
          altTag={altTag}
          brand={brand}
          productTile={productTile}
          onQuickAdd={handleQuickAdd}
          onQuickAddSubmit={recordQuickAddSubmitEvent}
          onChooseOption={handleChooseOption}
          onHeartClicked={recordHeartClickedEvent}
          onSaveToBoard={recordSaveToBoardSubmitEvent}
          onRemoveFromBoard={recordRemoveFromBoardSubmitEvent}
          onChooseOptionQuantityUpdate={handleChooseOptionQuantityUpdate}
          onProductClick={onProductClick}
          generateProductLinkProps={generateProductLinkProps}
          hideCartQuantity={hideCartQuantity}
          showVerticalImagery={showVerticalImagery}
          showPriceBadge={showPriceBadge}
          onTopLeftBadgeVisible={recordProductBadgeViewEvent}
          useLazyImageLoading={useLazyImageLoading}
        />
      </FallbackContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  min-width: 88px;
`;
